import { useEffect, useState } from "react";

import { useAuth } from "@/app/providers/AuthProvider";
import { useToast } from "@/app/providers/ToastProvider";
import { FormField } from "@/components/common/FormField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiClientError } from "@/lib/apiClient";
import { focusFirstField } from "@/lib/forms";

/** Typed once per shift on a shared device, so the last one is offered back. */
const LAST_ORGANIZATION_KEY = "last_organization";

/** The API answers in English; the counter staff should not see that. */
function toFrenchMessage(error: unknown): string {
  if (error instanceof ApiClientError) {
    if (error.statusCode === 401 || error.statusCode === 403) {
      return "Organisation, nom d’utilisateur ou mot de passe incorrect.";
    }
    if (error.statusCode === 404) {
      return "Cette organisation est introuvable.";
    }
    if (error.statusCode === 429) {
      const retryAfter = error.retryAfterSeconds;
      return retryAfter
        ? `Trop de tentatives. Réessayez dans ${retryAfter} seconde${retryAfter > 1 ? "s" : ""}.`
        : "Trop de tentatives. Réessayez dans un instant.";
    }
    if (error.statusCode >= 500) {
      return "Le service est momentanément indisponible. Réessayez dans un instant.";
    }
  }
  return "La connexion a échoué. Vérifiez votre saisie et réessayez.";
}

export default function LoginForm() {
  const [organizationId, setOrganizationId] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberOrganization, setRememberOrganization] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login } = useAuth();
  const toast = useToast();

  useEffect(() => {
    const saved = localStorage.getItem(LAST_ORGANIZATION_KEY);
    if (saved) setOrganizationId(saved);
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!organizationId.trim()) nextErrors.organizationId = "Renseignez votre organisation.";
    if (!username.trim()) nextErrors.username = "Renseignez votre nom d’utilisateur.";
    if (!password) nextErrors.password = "Renseignez votre mot de passe.";

    if (Object.keys(nextErrors).length > 0) {
      focusFirstField(Object.keys(nextErrors));
      toast.error(Object.values(nextErrors).join(" "));
      return;
    }
    setIsSubmitting(true);

    try {
      await login(organizationId.trim(), username.trim(), password);
      if (rememberOrganization) {
        localStorage.setItem(LAST_ORGANIZATION_KEY, organizationId.trim());
      } else {
        localStorage.removeItem(LAST_ORGANIZATION_KEY);
      }
      // The auth provider swaps the shell in as soon as the session lands.
    } catch (e) {
      toast.error(toFrenchMessage(e));
      setPassword("");
      setShowPassword(false);
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="flex w-full max-w-113 flex-col gap-5 rounded-2xl border border-border bg-card p-6 shadow-[0_8px_24px_-4px_rgba(0,0,0,0.06)] sm:p-9"
    >
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[28px] leading-9.75 font-semibold tracking-tight">Connexion</h1>
        <p className="text-[15px] text-muted-foreground">Entrez votre organisation et vos identifiants.</p>
      </div>

      <FormField label="Organisation" htmlFor="organizationId">
        <Input
          id="organizationId"
          name="organization"
          value={organizationId}
          autoComplete="organization"
          autoCapitalize="none"
          onChange={(e) => setOrganizationId(e.target.value)}
          className="h-13"
        />
      </FormField>

      <FormField label="Utilisateur" htmlFor="username">
        <Input
          id="username"
          name="username"
          value={username}
          autoComplete="username"
          autoCapitalize="none"
          onChange={(e) => setUsername(e.target.value)}
          className="h-13"
        />
      </FormField>

      <FormField label="Mot de passe" htmlFor="password">
        <div className="relative">
          <Input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            className="h-13 pr-24"
          />
          <button
            type="button"
            onClick={() => setShowPassword((visible) => !visible)}
            aria-pressed={showPassword}
            className="absolute top-1/2 right-4 -translate-y-1/2 text-[15px] font-semibold text-info hover:underline"
          >
            {showPassword ? "Masquer" : "Afficher"}
          </button>
        </div>
      </FormField>

      {/*
       * The design asks to stay signed in, which the API cannot do: the session
       * cookie is httpOnly with a fixed one-hour life and sign-in takes no such
       * flag. This keeps the row for what the screen can actually remember.
       */}
      <label htmlFor="rememberOrganization" className="flex items-center gap-3 text-[15px] font-medium">
        <input
          id="rememberOrganization"
          type="checkbox"
          checked={rememberOrganization}
          onChange={(e) => setRememberOrganization(e.target.checked)}
          className="size-6 shrink-0 rounded-md accent-brand-black"
        />
        Se souvenir de mon organisation
      </label>

      <Button type="submit" size="lg" className="h-14 w-full text-[18px]" disabled={isSubmitting}>
        {isSubmitting ? "Connexion…" : "Se connecter"}
      </Button>
    </form>
  );
}
